# A route transition that dies on an aborted request must not leave a blank
# screen — card 6aab65a36c29c3a6dc3bbbb5.
#
# THE DEFECT. The play route resolves `studentRefresh` and `games` before it
# mounts anything (app-route.coffee). Those are network reads, and when one is
# aborted mid-flight ui-router rejects the whole transition. Nothing anywhere in
# el-feet registered an `onError` hook, so the rejection went to the router's
# default handler — a console line — and the UIView simply stayed empty.
# Measured on QA elfeet 2026-09-17, 1 of 46 play-route loads at 932x430: a solid
# grey frame for the full 120s budget, `Transition Rejection($id: 0 type: 6 …
# AxiosError: Request aborted)` in the console, the interpreter never mounted.
# The immediate rerun of the same node loaded in 10s — the request is transient,
# the grey screen is not: nothing recovers it but a manual refresh, and the
# person looking at it is a child.
#
# WHAT THIS DOES. One retry, then say something. A transient aborted XHR is
# exactly the thing a retry fixes, and the retry is free; if it fails a second
# time the connection is genuinely gone and the honest move is to tell the
# person rather than to keep trying behind a blank page.
#
# WHAT IT DELIBERATELY LEAVES ALONE. Only `type: 6` (ERROR) is ours. A
# SUPERSEDED or ABORTED rejection is the router's normal traffic — the child
# tapped something else while this one was resolving — and retrying it would
# drag them back to a screen they left.

# @uirouter/core RejectType. Written out rather than imported: this module is
# loaded by three shells and its only ui-router dependency is this one number.
ERROR_REJECTION = 6

# An aborted or dropped request, as axios reports it. Anything else (a 500, a
# bad param, a throw inside a resolve) is a real failure and retrying it just
# fails again more slowly.
RETRYABLE_CODES = ['ECONNABORTED', 'ERR_NETWORK', 'ERR_CANCELED']

# EXPORTED because the same question is asked one step earlier. The app's boot
# read (`window.app.refresh()` in each shell's `initialize`) can be aborted by
# the same transient, and it happens BEFORE the router exists — so the hook
# below has not been installed yet and cannot help. Both places must agree on
# what "transient" means, or a request that is retried at the route is given up
# on at boot; one predicate, two callers (card 6aab772ced72ee7fceb166f3).
export isRetryable = (detail) ->
  return false unless detail?
  return true if detail.code? and detail.code in RETRYABLE_CODES
  message = detail.message ? String(detail)
  /request aborted|network error|timeout/i.test message

# THE BOOT READ, one step before the router exists.
#
# Each shell's `initialize` opens with `window.app.refresh()` — the read that
# fetches the login and the account — and only then starts React and the
# router. When THAT read is aborted by the same transient, the hook above is not
# installed yet and cannot help: in /app the rejection reaches
# `render_react_error` and the person gets the full "We have encountered an
# error" apology page, and in the teacher and org shells there was no `.catch`
# at all, so the rejection went nowhere and the page simply stayed blank.
# Measured on QA elfeet 2026-09-17: 1 of 5 headless play-route loads at 932x430
# (card 6aab772ced72ee7fceb166f3).
#
# One retry, same judgment as the hook. A transient aborted XHR is what a retry
# fixes; if the second read fails too the connection is genuinely gone, and the
# caller's own error path — an apology page the person can act on — is the
# honest answer. Anything that is NOT a dropped request (a 500, a throw inside
# refresh) is rethrown untouched and reaches that path immediately, because
# retrying it just fails again more slowly.
#
# `attempt` is a function, not a promise: the retry has to issue a NEW request,
# and a promise can only be awaited again.
export retryAbortedRead = (attempt, onRetry = null) ->
  Promise.resolve()
    .then attempt
    .catch (error) ->
      throw error unless isRetryable error
      onRetry? error
      attempt()

# `deps` exists so this is drivable without a browser (the pin supplies its own
# emit/report); the app passes nothing and gets the real toast and error track.
export installTransitionRecovery = (router, deps = {}) ->
  return false unless router?.transitionService?.onError?

  emit = deps.emit ? (message) -> window.app?.emitter?.emit? 'global toast', message, false
  report = deps.report ? (message) -> window.app?.trackError? message

  # One retry per target, not one retry ever: a second aborted load an hour
  # later on another node deserves its own retry.
  retried = {}

  router.transitionService.onError {}, (transition) ->
    rejection = transition?.error?()
    return unless rejection?.type is ERROR_REJECTION

    detail = rejection.detail
    target = transition.to?()
    name = target?.name ? target
    params = transition.params?() ? {}
    key = "#{name}:#{try JSON.stringify(params) catch then ''}"

    report "Route transition failed (#{name}): #{detail?.message ? rejection.message ? rejection}"

    unless isRetryable detail
      emit "Something went wrong opening this screen. Please reload the page."
      return

    if retried[key]
      # The retry failed too. Stop, and say so — a third attempt behind a blank
      # screen is indistinguishable from the defect this replaces.
      delete retried[key]
      emit "We could not load this screen. Please check your connection and reload the page."
      return

    retried[key] = true
    console.warn "Route transition to #{name} was aborted — retrying once"
    try
      p = router.stateService?.go name, params, {reload: true}
      p?.then?((-> delete retried[key]), (-> null))
    catch e
      delete retried[key]
      emit "We could not load this screen. Please check your connection and reload the page."
    return

  true
